'use client';

import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { ThroughputPoint } from '@/hooks/useDashboardRealtime';

interface Props {
  data: ThroughputPoint[];
  loading: boolean;
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ dataKey: string; color: string; value: number }>; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-surface-overlay border border-border rounded-lg px-3 py-2 shadow-xl text-xs">
        <p className="text-muted mb-1 font-mono">{label}</p>
        {payload.map((p) => (
          <div key={`tip-${p.dataKey}`} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-foreground-muted capitalize">{p.dataKey}:</span>
            <span className="font-semibold text-foreground font-mono">{p.value} Gbps</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function ThroughputChart({ data, loading }: Props) {
  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-foreground">Network Throughput</p>
          <p className="text-[11px] text-muted mt-0.5">
            Gbps · All regions · Last 3h
            {!loading && data.length > 0 && (
              <span className="inline-flex items-center gap-1 ml-2">
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse inline-block" />
                <span className="text-success">Live</span>
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-primary" />
            <span className="text-muted">Inbound</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-info" />
            <span className="text-muted">Outbound</span>
          </div>
        </div>
      </div>
      {loading && data.length === 0 ? (
        <div className="h-[180px] bg-surface-raised rounded animate-pulse" />
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gradInbound" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(189 94% 43%)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(189 94% 43%)" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="gradOutbound" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(213 94% 68%)" stopOpacity={0.25} />
                <stop offset="95%" stopColor="hsl(213 94% 68%)" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 14% 20%)" vertical={false} />
            <XAxis dataKey="time_label" tick={{ fontSize: 10, fill: 'hsl(222 10% 45%)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: 'hsl(222 10% 45%)' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="inbound" stroke="hsl(189 94% 43%)" strokeWidth={2} fill="url(#gradInbound)" dot={false} />
            <Area type="monotone" dataKey="outbound" stroke="hsl(213 94% 68%)" strokeWidth={2} fill="url(#gradOutbound)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}